import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import type { AuditLogAction } from "../types";

/**
 * Color spec is fixed by the product brief — keep all action color choices
 * in a single map so designers can re-tune without touching call sites.
 *
 * Tailwind palette is used here (instead of semantic tokens) because the
 * brief asks for distinct hues per action that don't all map cleanly to
 * the four semantic states (success/warning/danger/brand). The map is
 * the only place raw color classes live in this module.
 */
const ACTION_STYLE: Record<AuditLogAction, { bg: string; text: string; label: string }> = {
  create:      { bg: "bg-green-100",  text: "text-green-800",  label: "Create" },
  update:      { bg: "bg-blue-100",   text: "text-blue-800",   label: "Update" },
  delete:      { bg: "bg-red-100",    text: "text-red-800",    label: "Delete" },
  associate:   { bg: "bg-purple-100", text: "text-purple-800", label: "Associate" },
  dissociate:  { bg: "bg-orange-100", text: "text-orange-800", label: "Dissociate" },
  bulk_update: { bg: "bg-indigo-100", text: "text-indigo-800", label: "Bulk update" },
  claim:       { bg: "bg-teal-100",   text: "text-teal-800",   label: "Claim" },
};

const FALLBACK_STYLE = {
  bg: "bg-gray-100",
  text: "text-gray-700",
  label: "Unknown",
};

interface ActionChipProps {
  action: AuditLogAction | string;
  size?: "small" | "medium";
}

export const ActionChip: React.FC<ActionChipProps> = ({
  action,
  size = "small",
}) => {
  const style =
    (ACTION_STYLE as Record<string, typeof FALLBACK_STYLE>)[action] ??
    { ...FALLBACK_STYLE, label: String(action) };

  return (
    <GenericTag
      label={style.label}
      variant="custom"
      backgroundColor={style.bg}
      textColor={style.text}
      size={size}
      rounded="full"
    />
  );
};

export default ActionChip;
